import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Tooltip } from 'reactstrap';
import { DatedRatePeriodMandatoryItemModel, OverviewRateModel } from '../../api/pricing/pricing.types';
import { RateSquareInfo } from './RateSquare';
import dayjs from 'dayjs';
import { WithTranslation, withTranslation } from 'react-i18next';

interface RateTooltipProps extends WithTranslation {
  rateSquareInfo: RateSquareInfo | undefined;
  isTooltipOpen: boolean;
  rateInfo: OverviewRateModel | undefined;
  isLoading: boolean;
  target: any;
  toggle: () => void;
}

class RateTooltip extends PureComponent<RateTooltipProps> {

  getTooltipDate = (date: string) => `${dayjs(date).format('dddd').substring(0, 3)}, ${dayjs(date)
    .format('MMMM').substring(0, 3)} ${dayjs(date).date().toString()}`;

  openStopSellUrl = (id: number) => {
    const newWindow = window.open(`/rates/stopsells/${id}/view`, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  getMandatoryItems = (mandatoryItems: DatedRatePeriodMandatoryItemModel[]) => {
    const elements: React.JSX.Element[] = [];
    mandatoryItems.map((mandatoryItem) => {
        elements.push(<div className="d-flex ml-1 row justify-content-between">
          <label>{mandatoryItem.name}:</label>
          <div className="ml-2 text-primary">{mandatoryItem.price.toFixed(2)}</div>
        </div>);
      }
    );
    return elements;
  };

  formatPercentage = (num: number) => {
    return (num * 100).toFixed(2).replace(/\.00$/, '');
  };

  getTooltipContent = (info?: RateSquareInfo) => {
    const { t } = this.props;

    if (info) {

      if (info.stopSells.length) {

        const stopSellList: JSX.Element[] = [];
        info.stopSells.forEach((ss) => {
          stopSellList.push((
            <>
              <Link
                to={`/rates/stopsells/${ss.stopSellId}/view`}
                target="_blank"
                rel="noopener noreferrer">{`${this.getTooltipDate(ss.originalStartDateTime)} - ${this.getTooltipDate(ss.originalEndDateTime)}`}
              </Link>
            </>
          ));
        });

        return (
          <>
            <div className="d-flex justify-content-center align-items-center">
              <label className="font-weight-bold mb-1">{this.getTooltipDate(info.date)}</label>
            </div>
            <label style={{ fontSize: '20px', color: 'black' }}>{t('components.rate_tooltip.stop_sell')}</label>
            {stopSellList}
          </>
        );
      }

      if (this.props.isLoading) {
        return (
          <>
            <label>{this.getTooltipDate(info.date)}</label>
            <div className="row justify-content-between pl-1 pr-1">
              <label>{t('components.rate_tooltip.standard_rate')}:</label>
              <div className="ml-2">{info.rateDetails.totalPerDay.toString().substring(0, 5)}</div>
            </div>
            <div className="d-flex justify-content-center align-items-center">
              <Spinner animation="border" variant="info"/>
            </div>
          </>
        );
      }

      let rateInfo;
      if (this.props.rateInfo) rateInfo = this.props.rateInfo as unknown as OverviewRateModel;

      if (rateInfo) {
        return (
          <div className="pr-2">
            <div className="d-flex ml-1 justify-content-center align-items-center">
              <label className="filter-label">{this.getTooltipDate(info.date)}</label>
            </div>
            <div className="d-flex ml-1 row justify-content-between">
              <label>{t('components.rate_tooltip.rate_plan')}:</label>
              <div className="ml-2 text-primary">{info.rateDetails.baseAmount.toFixed(2)}</div>
            </div>
            {info.rateDetails.baseRateAdjustmentPercentage !== 0 && (
              <div className="d-flex ml-1 row justify-content-between">
                <label>{t('components.rate_tooltip.base_rate_adjustment')} ({this.formatPercentage(info.rateDetails.baseRateAdjustmentPercentage)}%):</label>
                <div className="ml-2 text-primary">{info.rateDetails.baseRateAdjustmentAmount.toFixed(2)}</div>
              </div>
            )}
            {info.rateDetails.discountPercentage !== 0 && (
              <div className="d-flex ml-1 row justify-content-between">
                <label>{t('components.rate_tooltip.discount')} (-{this.formatPercentage(info.rateDetails.discountPercentage)}%):</label>
                <div className="ml-2 text-primary">-{info.rateDetails.discountAmount.toFixed(2)}</div>
              </div>
            )}
            {info.rateDetails.safPercentage !== 0 && (
              <div className="d-flex ml-1 row justify-content-between">
                <label>{t('components.rate_tooltip.saf')} ({this.formatPercentage(info.rateDetails.safPercentage)}%):</label>
                <div className="ml-2 text-primary">{info.rateDetails.safAmount.toFixed(2)}</div>
              </div>
            )}
            <div className="d-flex ml-1 row justify-content-between">
              <label>{t('components.rate_tooltip.vat')} ({this.formatPercentage(info.rateDetails.vatPercentage)}%):</label>
              <div className="ml-2 text-primary">{info.rateDetails.vatAmount.toFixed(2)}</div>
            </div>
            <div className="d-flex ml-1 row justify-content-between mt-1 mb-1">
              <label>{t('components.rate_tooltip.rate_total')}:</label>
              <div className="ml-2 text-bold">{info.rateDetails.adjustedVehicleBaseRateAmount.toFixed(2)}</div>
            </div>
            {this.getMandatoryItems(info.rateDetails.mandatoryItems)}
            <div className="d-flex ml-1 row justify-content-between mt-1">
              <label>{t('components.rate_tooltip.mandatory_item_total')}:</label>
              <div className="ml-2 text-bold">{info.rateDetails.mandatoryItemPriceTotal.toFixed(2)}</div>
            </div>
            <div className="d-flex ml-1 mt-1 row justify-content-between align-items-center">
              <label>{t('components.rate_tooltip.total_per_day')}:</label>
              <div className="ml-2 text-highlgiht">{info.rateDetails.totalPerDay.toFixed(2)}</div>
            </div>
          </div>
        );
      }
    }

    return (<></>);
  };

  render() {
    return (
      <Tooltip
        style={{ minWidth: '256px', maxWidth: '400px', minHeight: '140px' }}
        placement="top"
        target={this.props.target}
        isOpen={this.props.isTooltipOpen}
      >
        <div className="d-flex flex-column p-1">
          <div className="d-flex justify-content-end pb-1">
            <button className="modal-close-btn" type="button" onClick={this.props.toggle}> x</button>
          </div>
          {this.getTooltipContent(this.props.rateSquareInfo)}
        </div>
      </Tooltip>
    );
  }
}

export default withTranslation()(RateTooltip);
